import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { CheckCircle, CircleNotch, WarningCircle } from "@phosphor-icons/react";

export default function PaymentSuccess() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("polling"); // polling | paid | expired | error
    const [plan, setPlan] = useState(null);
    const [returnTo, setReturnTo] = useState(null);
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let timer;
        const poll = async () => {
            attempts.current += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") { setPlan(data.plan); setReturnTo(data.return_to || null); setState("paid"); return; }
                if (["expired", "failed"].includes(data.payment_status)) { setState("expired"); return; }
            } catch { /* keep polling */ }
            if (attempts.current >= 10) { setState("error"); return; }
            timer = setTimeout(poll, 2000);
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    return (
        <Layout>
            <div className="max-w-[560px] mx-auto text-center py-16" data-testid="payment-success-page">
                {state === "polling" && (
                    <>
                        <CircleNotch size={44} className="mx-auto animate-spin text-[#7C3AED]" />
                        <h1 className="font-display-black text-2xl mt-6">Verifica del pagamento…</h1>
                        <p className="text-sm text-gray-500 mt-2">Attendi qualche secondo, stiamo confermando la transazione con Stripe.</p>
                    </>
                )}
                {state === "paid" && (
                    <>
                        <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500" />
                        <h1 className="font-display-black text-2xl mt-6" data-testid="payment-confirmed-title">
                            {plan === "standard" ? "Standard è attivo." : "Pagamento confermato!"}
                        </h1>
                        <p className="text-sm text-gray-500 mt-2">
                            Il piano <b className="uppercase">{plan}</b> è ora attivo sulla tua organizzazione.
                        </p>
                        {returnTo ? (
                            <>
                                <button onClick={() => navigate(returnTo)} data-testid="payment-return-to-auction"
                                        className="mt-8 h-11 px-6 bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white text-sm font-semibold rounded-lg">
                                    Analizza questa opportunità
                                </button>
                                <div>
                                    <button onClick={() => navigate("/dashboard")} data-testid="payment-success-dashboard"
                                            className="mt-3 text-xs text-gray-500 underline">
                                        oppure vai alla Dashboard
                                    </button>
                                </div>
                            </>
                        ) : (
                            <button onClick={() => navigate("/dashboard")} data-testid="payment-success-dashboard"
                                    className="mt-8 h-11 px-6 bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white text-sm font-semibold rounded-lg">
                                Vai alla Dashboard
                            </button>
                        )}
                    </>
                )}
                {(state === "expired" || state === "error") && (
                    <>
                        <WarningCircle size={48} weight="fill" className="mx-auto text-amber-500" />
                        <h1 className="font-display-black text-2xl mt-6">
                            {state === "expired" ? "Pagamento non completato" : "Verifica non riuscita"}
                        </h1>
                        <p className="text-sm text-gray-500 mt-2">
                            {state === "expired"
                                ? "La sessione di pagamento è scaduta o è stata annullata. Nessun addebito effettuato."
                                : "Non siamo riusciti a confermare il pagamento. Se l'addebito è avvenuto, il piano verrà attivato automaticamente a breve."}
                        </p>
                        <button onClick={() => navigate("/piano")} data-testid="payment-retry"
                                className="mt-8 h-11 px-6 bg-white/10 border border-white/20 text-sm font-semibold rounded-lg">
                            Torna ai piani
                        </button>
                    </>
                )}
            </div>
        </Layout>
    );
}
